/**
 * PresupuestosApiClient -- catalogo de productos + presupuestos de obra.
 * Mision: AGENTE-INSIGNIA-V1-001 / PRESU-UI-T1 fase A (ASSIGN #8342).
 *
 * CONTRATO: el de PRESU-API-T1 (Argy-A, #8343) tal cual lo escribio Ares --
 * NO se invento ninguno (patron PANEL-AUDIOS). Se construye en paralelo, asi
 * que cada metodo degrada FAIL-OPEN con NOT_IMPLEMENTED mientras no exista:
 * la UI nace honesta y se enciende SOLA cuando la API publique, sin re-deploy.
 *
 * Fuente: nexo_projects (Postgres) -- 49 productos y el presupuesto JJ 1271 v4
 * del negocio demo ya poblados.
 *
 * @module infrastructure/api/PresupuestosApiClient
 */

import { ApiBase } from './ApiBase.js';
import { getSelectedBusiness, isValidBusinessId } from '../negocioElegido.js';
import { AppConfig } from '../../config.js';

const BASE = '/api/v1/presupuestos';

/**
 * UNA sola fuente de negocio para TODA la vista: el selector de arriba.
 *
 * Lo manda cada llamada -- GET y POST -- y el backend lo valida con membresia
 * activa en business_users (ResolverNegocioAsync), asi que un admin solo puede
 * operar negocios a los que de verdad pertenece. Antes solo lo mandaban 4 de
 * los 10 metodos: el catalogo obedecia al selector y el upload al token, y un
 * admin cuyo token no traia Maderinco se comia un 403 sin entender por que
 * (reporte de Josse, ASSIGN #8561).
 *
 * Los GET ya estan vivos en produccion. Los POST (guardar, crear, upload,
 * solicitar-pdf) los esta publicando Argy-A en PRESU-API-T4: mientras tanto
 * ASP.NET ignora el parametro que no conoce, asi que mandarlo es inofensivo y
 * se encienden SOLOS el dia que despliegue, sin re-deploy del panel.
 */
function paramNegocio(sep) {
  const id = getSelectedBusiness();
  return isValidBusinessId(id) ? `${sep}negocio=${encodeURIComponent(id)}` : '';
}

/** Distingue "no existe aun" de "no tienes permiso" (leccion del banner que mentia). */
function clasificar(res) {
  // La API dice POR QUE rechazo en el ProblemDetails (NEGOCIO_AJENO, SIN_NEGOCIO,
  // SIN_IDENTIDAD...) y ApiBase ya me pasa ese cuerpo en res.details. Leerlo
  // primero evita meter todo 403 en la misma bolsa de "no tienes permiso".
  const preciso = codigoDelProblema(res.details, '');
  if (preciso && preciso !== 'PROBLEMS') return { ok: false, error: { code: preciso, detalle: res.details?.detail ?? '' } };
  const t = JSON.stringify(res.error || '').toLowerCase();
  if (t.includes('404') || t.includes('not found') || t.includes('no existe')) {
    return { ok: false, error: { code: 'NOT_IMPLEMENTED' } };
  }
  if (t.includes('401') || t.includes('403') || t.includes('token') ||
      t.includes('autentic') || t.includes('autoriz') || t.includes('permis')) {
    return { ok: false, error: { code: 'FORBIDDEN' } };
  }
  return res;
}

/**
 * Codigo del ProblemDetails: `type` es https://nexoapi.com/problems/<codigo>.
 * Se lee la ULTIMA parte y se sube a mayusculas. Si no viene, queda el status.
 */
function codigoDelProblema(cuerpo, status) {
  const tipo = cuerpo?.type ?? cuerpo?.Type ?? '';
  const ultima = String(tipo).split('/').filter(Boolean).pop() || '';
  return /^[a-z_]+$/i.test(ultima) && ultima !== 'problems' ? ultima.toUpperCase() : String(status);
}

/** items[] directo, o envuelto en data / data.items (no se cual mandara Argy-A). */
function listaDe(res) {
  const p = res.data?.data ?? res.data ?? {};
  return Array.isArray(p.items) ? p.items
       : Array.isArray(p) ? p
       : Array.isArray(p.data) ? p.data
       : Array.isArray(p.productos) ? p.productos
       : Array.isArray(p.presupuestos) ? p.presupuestos : [];
}

/**
 * Manda un archivo JSON como cuerpo SIN leerlo ni reformatearlo (la API saca el sha256 del texto recibido) y traduce la
 * respuesta: el 403 puede ser PUERTA_CERRADA (no es el demo) o el rol, por eso se lee el ProblemDetails primero.
 */
async function mandarArchivoTalCual(ruta, file) {
    if (!file) return { ok: false, error: { code: 'ARCHIVO_REQUERIDO' } };
    const headers = { 'Content-Type': 'application/json' };
    if (AppConfig.token) headers['Authorization'] = `Bearer ${AppConfig.token}`;
    try {
      const res = await ApiBase.fetchQueCambia(`${AppConfig.apiBase}${BASE}/${ruta}${paramNegocio('?')}`, { method: 'POST', body: file, headers });
      const cuerpo = await res.json().catch(() => null);
      if (res.ok) return { ok: true, data: cuerpo?.data ?? cuerpo?.Data ?? cuerpo ?? {} };
      if (res.status === 401) return { ok: false, error: { code: 'SIN_SESION' } };
      // El 403 puede ser PUERTA_CERRADA (no es el negocio demo) o el rol: se lee el ProblemDetails primero.
      const code = codigoDelProblema(cuerpo, res.status);
      if (res.status === 404 && code === '404') return { ok: false, error: { code: 'NOT_IMPLEMENTED' } };
      if (res.status === 403 && code === '403') return { ok: false, error: { code: 'FORBIDDEN' } };
      return { ok: false, error: { code, detalle: cuerpo?.detail ?? cuerpo?.Detail ?? '' } };
    } catch (e) {
      return { ok: false, error: { code: 'NETWORK', message: String(e).slice(0, 120) } };
    }
}

export const PresupuestosApiClient = {
  /**
   * (1b) GET /plantilla -- las secciones de Maderinco (Cubierta primero) con sus items de referencia (nexo-api 551,
   * MADERINCO-SECCIONES-API-T1). Solo el negocio demo: fuera, el codigo PUERTA_CERRADA; sin la ruta, NOT_IMPLEMENTED.
   * data = {catalogoSha, totalItems, secciones[]} tal cual (lo ordena application/presupuestos/plantilla.js).
   */
  async plantilla() {
    const res = await ApiBase.get(`${BASE}/plantilla${paramNegocio('?')}`);
    if (!res.ok) return clasificar(res);
    return { ok: true, data: res.data?.data ?? res.data ?? {} };
  },

  /**
   * Los capitulos de la lista del cliente con sus lineas (MADERINCO-LISTA-ITEMS-SECCIONES-T1; nexo-api 640, servido
   * en 1.6.877). Solo demo. Sin lista cargada viene vacio: el panel usa su archivo (lineasDelCliente.js).
   */
  async capitulos() {
    const res = await ApiBase.get(`${BASE}/plantilla/capitulos${paramNegocio('?')}`);
    if (!res.ok) return clasificar(res);
    return { ok: true, data: res.data?.data ?? res.data ?? {} };
  },

  /**
   * ITEM-ESTANDAR-EDITOR-T1: el item estandar de Maderinco por capitulo y linea, con sus por clasificar (nexo-api 785,
   * nodo tbl-item-estandar; solo demo). Lo lee application/presupuestos/itemEstandar.js (leerItemEstandar).
   */
  async itemEstandar() {
    const res = await ApiBase.get(`${BASE}/plantilla/item-estandar${paramNegocio('?')}`);
    if (!res.ok) return clasificar(res);
    return { ok: true, data: res.data?.data ?? res.data ?? {} };
  },

  /**
   * MAD-ELIMINAR-ITEM-T1 (nexo-api 795, #43829): en cuantos presupuestos esta el estandar ({ presupuestos }), para la
   * pregunta antes de borrar. Casa los presupuestos del negocio: tarda unos segundos.
   */
  async usoItemEstandar(id) {
    const res = await ApiBase.get(`${BASE}/plantilla/item-estandar/${encodeURIComponent(String(id ?? ''))}/uso${paramNegocio('?')}`);
    if (!res.ok) return clasificar(res);
    return { ok: true, data: res.data?.data ?? res.data ?? {} };
  },

  /**
   * MAD-ELIMINAR-ITEM-T1 (nexo-api 795): borra el estandar con la revision que se leyo; si un presupuesto lo usa, lo archiva.
   * Responde { eliminado, archivado, presupuestos, items_por_clasificar }; 409 ITEM_ESTANDAR_REVISION si cambio.
   */
  /**
   * MAD-TIPO-TARJETA-APARIENCIA-T1 (contrato propuesto a A #44460): PUT plantilla/item-estandar/lineas/{n}/tono?negocio con
   * { tipo, tono } (tono de la paleta o null = como sale hoy). Devuelve los tonos de la linea.
   */
  async guardarTonoDeTipo(n, cuerpo) {
    const res = await ApiBase.put(`${BASE}/plantilla/item-estandar/lineas/${encodeURIComponent(String(n ?? ''))}/tono${paramNegocio('?')}`, cuerpo);
    if (!res.ok) return clasificar(res);
    return { ok: true, data: res.data?.data ?? res.data ?? {} };
  },

  async borrarItemEstandar(id, revision) {
    const res = await ApiBase.del(`${BASE}/plantilla/item-estandar/${encodeURIComponent(String(id ?? ''))}?revision=${encodeURIComponent(String(revision ?? ''))}${paramNegocio('&')}`);
    if (!res.ok) return clasificar(res);
    return { ok: true, data: res.data?.data ?? res.data ?? {} };
  },

  /**
   * Guardar y crear un item estandar: forma propuesta a A en #43286 (PUT y POST sobre la misma ruta). Mientras la API no
   * las sirva responde 404 y clasificar lo vuelve NOT_IMPLEMENTED: el editor dice "falta publicar" y no pierde lo escrito.
   */
  async guardarItemEstandar(id, cuerpo) {
    const res = await ApiBase.put(`${BASE}/plantilla/item-estandar/${encodeURIComponent(String(id ?? ''))}${paramNegocio('?')}`, cuerpo);
    if (!res.ok) return clasificar(res);
    return { ok: true, data: res.data?.data ?? res.data ?? {} };
  },

  async crearItemEstandar(cuerpo) {
    const res = await ApiBase.post(`${BASE}/plantilla/item-estandar${paramNegocio('?')}`, cuerpo);
    if (!res.ok) return clasificar(res);
    return { ok: true, data: res.data?.data ?? res.data ?? {} };
  },

  /**
   * MAD-CLASIFICAR-ITEMS-T1 (nexo-api 797): el item de la plantilla itemId pasa al estandar id y a su linea. Responde el
   * estandar con el item en su lista; 404 ITEM_ESTANDAR_NO_EXISTE si alguno no es del negocio o el estandar esta archivado.
   */
  /**
   * MAD-CLASIFICAR-ITEMS-T1 (nexo-api 798, DECISION #44081): crea un estandar en la linea con el nombre a mano y le clasifica
   * el item de la plantilla en una sola transaccion. cuerpo = { linea, nombreManual, tipo?, caracteristicas?, precio?,
   * vigenteDesde? }. Responde el estandar con el item; 409 ITEM_ESTANDAR_REPETIDO si ya hay uno con ese nombre y datos;
   * 404 si el item o la linea no son del negocio. Si falla, no queda nada creado.
   */
  /**
   * MAD-ITEM-NUEVO-T1 (DECISION #44142): un item desde cero en una linea. En una transaccion la base crea el item de la
   * plantilla (para que salga como tarjeta), el estandar con el nombre a mano y la asignacion. cuerpo = { linea,
   * nombreManual, unidad?, precio?, vigenteDesde?, tipo?, caracteristicas? }; responde el estandar con su item; 409
   * ITEM_ESTANDAR_REPETIDO por la clave de la 031.
   */
  async crearItemNuevo(cuerpo) {
    const res = await ApiBase.post(`${BASE}/plantilla/item-estandar/nuevo${paramNegocio('?')}`, cuerpo);
    if (!res.ok) return clasificar(res);
    return { ok: true, data: res.data?.data ?? res.data ?? {} };
  },

  async nuevoDesdeItem(itemId, cuerpo) {
    const res = await ApiBase.post(`${BASE}/plantilla/item-estandar/nuevo-desde-item/${encodeURIComponent(String(itemId ?? ''))}${paramNegocio('?')}`, cuerpo);
    if (!res.ok) return clasificar(res);
    return { ok: true, data: res.data?.data ?? res.data ?? {} };
  },

  async clasificarItemEstandar(id, itemId) {
    const res = await ApiBase.post(`${BASE}/plantilla/item-estandar/${encodeURIComponent(String(id ?? ''))}/items/${encodeURIComponent(String(itemId ?? ''))}${paramNegocio('?')}`, {});
    if (!res.ok) return clasificar(res);
    return { ok: true, data: res.data?.data ?? res.data ?? {} };
  },

  /**
   * (1c) POST /plantilla/cargar -- el catalogo aprobado, TAL CUAL (MADERINCO-CARGA-CATALOGO-UI-T1).
   * La API calcula el sha256 sobre el texto recibido: si cambia un byte responde 409 CATALOGO_NO_APROBADO. Por eso el
   * File va como cuerpo sin leerlo ni parsearlo (ApiBase hace JSON.stringify y lo reformatearia). Gerente o admin.
   */
  async cargarPlantilla(file) {
    return mandarArchivoTalCual('plantilla/cargar', file);
  },

  /**
   * POST /plantilla/capitulos/cargar (nexo-api 640): la lista de capitulos aprobada, TAL CUAL (mismo camino que el
   * catalogo, Ares #36434). 200 = {listaSha, capitulos, lineas}; 409/422/400 con su codigo en error.code.
   */
  async cargarCapitulos(file) {
    return mandarArchivoTalCual('plantilla/capitulos/cargar', file);
  },

  /**
   * (1d) POST /plantilla/pilotos/{piloto}/casar?confirmar=true -- crea y casa los productos del piloto (nexo-api 571,
   * MADERINCO-PRODUCTO-SECCION-CONTRATO-T1). Solo el demo; repetir no crea mas. data = CasarPilotoDto tal cual.
   */
  /**
   * (1e) GET/PUT /productos/{id}/ubicaciones -- donde aparece el producto en el presupuesto (nexo-api 568,
   * MADERINCO-PRODUCTO-SECCION-CONTRATO-T1). El PUT reemplaza todo y exige la revision leida (409 VERSION_CAMBIO).
   * El fallo devuelve los DETALLES del ProblemDetails: ahi vienen los errores por fila (extensions.filas).
   */
  async leerUbicaciones(productoId) {
    const res = await ApiBase.get(`${BASE}/productos/${encodeURIComponent(productoId)}/ubicaciones${paramNegocio('?')}`);
    if (!res.ok) return { ...clasificar(res), detalles: res.details || null };
    return { ok: true, data: res.data?.data ?? res.data ?? {} };
  },

  async guardarUbicaciones(productoId, cuerpo) {
    const res = await ApiBase.put(`${BASE}/productos/${encodeURIComponent(productoId)}/ubicaciones${paramNegocio('?')}`, cuerpo);
    if (!res.ok) return { ...clasificar(res), detalles: res.details || null, status: res.code };
    return { ok: true, data: res.data?.data ?? res.data ?? {} };
  },

  /** (1f) GET /plantilla/secciones/{seccionId}/productos -- la vista por seccion (nexo-api 568). */
  async productosDeSeccion(seccionId) {
    const res = await ApiBase.get(`${BASE}/plantilla/secciones/${encodeURIComponent(seccionId)}/productos${paramNegocio('?')}`);
    if (!res.ok) return { ...clasificar(res), detalles: res.details || null };
    return { ok: true, data: res.data?.data ?? res.data ?? {} };
  },

  async casarPiloto(piloto) {
    const res = await ApiBase.post(`${BASE}/plantilla/pilotos/${encodeURIComponent(piloto)}/casar?confirmar=true${paramNegocio('&')}`, {});
    if (!res.ok) return clasificar(res);
    return { ok: true, data: res.data?.data ?? res.data ?? {} };
  },

  /** MADERINCO-CATALOGO-UNICO-T1 (nexo-api 577): casa TODA la plantilla con Mis items. Crea productos: pide confirmar. */
  async casarPlantilla() {
    const res = await ApiBase.post(`${BASE}/plantilla/casar?confirmar=true${paramNegocio('&')}`, {});
    if (!res.ok) return clasificar(res);
    return { ok: true, data: res.data?.data ?? res.data ?? {} };
  },

  /** MADERINCO-CATALOGO-UNICO-T1: confirma pares { itemId, productoId } (1 a 1000) que una persona eligio. */
  async confirmarCasados(pares) {
    const body = { items: (pares || []).map((p) => ({ itemId: p.itemId, productoId: p.productoId })) };
    const res = await ApiBase.post(`${BASE}/plantilla/casar/confirmar${paramNegocio('?')}`, body);
    if (!res.ok) return clasificar(res);
    return { ok: true, data: res.data?.data ?? res.data ?? {} };
  },

  /**
   * PRESU-IMPORTADOR-PANEL-T1 (nexo-api 584): sube el Excel y recibe la VISTA PREVIA (no escribe). Multipart con fetch
   * directo y token, como subirPdf (ApiBase fuerza JSON).
   */
  async importarVistaPrevia(file) {
    if (!file) return { ok: false, error: { code: 'ARCHIVO_REQUERIDO' } };
    const fd = new FormData();
    fd.append('archivo', file, file.name);
    const headers = {};
    if (AppConfig.token) headers['Authorization'] = `Bearer ${AppConfig.token}`;
    try {
      const res = await ApiBase.fetchQueCambia(`${AppConfig.apiBase}${BASE}/plantilla/importar/vista-previa${paramNegocio('?')}`, { method: 'POST', body: fd, headers });
      const cuerpo = await res.json().catch(() => null);
      if (res.ok) return { ok: true, data: cuerpo?.data ?? cuerpo?.Data ?? cuerpo ?? {} };
      if (res.status === 404) return { ok: false, error: { code: 'NOT_IMPLEMENTED' } };
      if (res.status === 401) return { ok: false, error: { code: 'SIN_SESION' } };
      if (res.status === 403) return { ok: false, error: { code: 'FORBIDDEN' } };
      return { ok: false, error: { code: codigoDelProblema(cuerpo, res.status), detalle: cuerpo?.detail ?? cuerpo?.Detail ?? '' } };
    } catch (e) {
      return { ok: false, error: { code: 'NETWORK', message: String(e).slice(0, 120) } };
    }
  },

  /** PRESU-IMPORTADOR-PANEL-T1: guarda solo las filas aprobadas. Con una invalida no guarda nada (400/409 con filas). */
  async importarConfirmar(filas) {
    const res = await ApiBase.post(`${BASE}/plantilla/importar/confirmar${paramNegocio('?')}`, { filas });
    if (!res.ok) return { ...clasificar(res), details: res.details || null };
    return { ok: true, data: res.data?.data ?? res.data ?? {} };
  },

  /** (1) GET /productos?buscar= -- catalogo del negocio del token. */
  async listarProductos({ buscar = '', conUbicaciones = false } = {}) {
    // MADERINCO-MIS-ITEMS-REDISENO-T1 (nexo-api 579): conUbicaciones trae seccion y paso de cada producto en la misma consulta.
    const partes = [buscar ? `buscar=${encodeURIComponent(buscar)}` : '', conUbicaciones ? 'conUbicaciones=true' : ''].filter(Boolean);
    const qs = partes.length ? `?${partes.join('&')}` : '';
    const res = await ApiBase.get(`${BASE}/productos${qs}${paramNegocio(qs ? '&' : '?')}`);
    if (!res.ok) return clasificar(res);
    return { ok: true, data: listaDe(res) };
  },

  /**
   * (2) POST/PUT /productos -- crear o editar un producto del catalogo.
   *
   * LAS CLAVES VAN EN camelCase, NO en snake_case. GuardarProductoRequest es
   * PascalCase y ASP.NET liga case-INsensitive, pero NO quita guiones bajos:
   * costo_unitario no liga con CostoUnitario y el precio llegaba en null sin
   * que nadie protestara. Verificado en origin/main: no hay politica de nombres
   * en Program.cs ni JsonPropertyName en estos DTOs.
   */
  async guardarProducto(producto) {
    const body = {
      nombre: producto.nombre,
      descripcion: producto.descripcion ?? null,
      unidad: producto.unidad ?? null,
      costoUnitario: producto.costoUnitario ?? null,
      precioVenta: producto.precioVenta ?? null,
      precioMayor: producto.precioMayor ?? null,
      mayorDesdeUnidades: producto.mayorDesdeUnidades ?? null,
      activo: producto.activo ?? true
    };
    // El negocio lo manda el SELECTOR, igual que los GET. Sin esto el backend
    // lo resolvia por token y un admin que opera OTRO negocio escribia en el
    // suyo -- o se comia un 403 sin entender por que (ASSIGN #8561).
    const res = producto.id
      ? await ApiBase.put(`${BASE}/productos/${encodeURIComponent(producto.id)}${paramNegocio('?')}`, body)
      : await ApiBase.post(`${BASE}/productos${paramNegocio('?')}`, body);
    return res.ok ? res : clasificar(res);
  },

  /** (3) POST / -- crear presupuesto con secciones e items (payload anidado). */
  async crearPresupuesto(presupuesto) {
    // El cuerpo se arma AQUI, no en la vista: es el unico sitio que conoce el
    // contrato. Antes la vista mandaba cliente_nombre / utilidad_pct / area_m2
    // y ninguna ligaba con el DTO (ClienteNombre es [Required], asi que el POST
    // respondia 400 y guardar NUNCA funciono contra la API real).
    const cuerpo = {
      // Mismo codigo + version+1 = version nueva del mismo presupuesto.
      // Verificado en origin/main: el INSERT toma @Codigo y @Version del cuerpo.
      ...(presupuesto.codigo ? { codigo: presupuesto.codigo } : {}),
      ...(presupuesto.version ? { version: Number(presupuesto.version) } : {}),
      proyecto: presupuesto.proyecto ?? null,
      clienteNombre: presupuesto.clienteNombre ?? '',
      utilidadPct: Number(presupuesto.utilidadPct) || 0,
      ivaUtilidadPct: Number(presupuesto.ivaUtilidadPct) || 0,
      areaM2: presupuesto.areaM2 ? Number(presupuesto.areaM2) : null,
      descuento: Number(presupuesto.descuento) || 0,
      secciones: (presupuesto.secciones || []).map((s, i) => ({
        orden: s.orden ?? i + 1,
        nombre: s.nombre,
        esAdicional: !!s.esAdicional,
        items: (s.items || []).map((it, j) => ({
          productoId: it.productoId ?? null,
          orden: it.orden ?? j + 1,
          titulo: it.titulo,
          descripcion: it.descripcion || null,
          unidad: it.unidad ?? null,
          cantidad: Number(it.cantidad) || 0,
          precioUnit: Number(it.precioUnit) || 0
        }))
      }))
    };
    const res = await ApiBase.post(`${BASE}${paramNegocio('?')}`, cuerpo);
    return res.ok ? res : clasificar(res);
  },

  /** (4) GET /?codigo= y GET /{id} -- leer con secciones + items + AIU del server. */
  async listarPresupuestos({ codigo = '' } = {}) {
    const qs = codigo ? `?codigo=${encodeURIComponent(codigo)}` : '';
    const res = await ApiBase.get(`${BASE}${qs}${paramNegocio(qs ? '&' : '?')}`);
    if (!res.ok) return clasificar(res);
    return { ok: true, data: listaDe(res) };
  },

  /**
   * REPLICAR-PRESUPUESTO-T1: GET /{id}/item-estandar (nexo-api 792) -- por partida su item estandar y la confianza
   * (producto, plantilla_item, texto, por_clasificar). Sin la ruta servida responde NOT_IMPLEMENTED y la replica sigue
   * sin tipo.
   */
  async casamientoItemEstandar(id) {
    if (!id) return { ok: false, error: { code: 'ID_REQUERIDO' } };
    const res = await ApiBase.get(`${BASE}/${encodeURIComponent(id)}/item-estandar${paramNegocio('?')}`);
    if (!res.ok) return clasificar(res);
    return { ok: true, data: res.data?.data ?? res.data ?? null };
  },

  async obtenerPresupuesto(id) {
    if (!id) return { ok: false, error: { code: 'ID_REQUERIDO' } };
    const res = await ApiBase.get(`${BASE}/${encodeURIComponent(id)}${paramNegocio('?')}`);
    if (!res.ok) return clasificar(res);
    return { ok: true, data: res.data?.data ?? res.data ?? null };
  },

  /**
   * (7) POST /{id}/clonar -- copia un presupuesto a un borrador nuevo editable.
   *
   * Lo esta construyendo Argy-A (ASSIGN #8477). NO lo invento: mientras no
   * exista responde 404 y esto degrada a NOT_IMPLEMENTED, asi que el boton dice
   * la verdad y se enciende solo el dia que publiquen, sin re-deploy del panel.
   */
  async clonarPresupuesto(id) {
    if (!id) return { ok: false, error: { code: 'ID_REQUERIDO' } };
    const res = await ApiBase.post(`${BASE}/${encodeURIComponent(id)}/clonar${paramNegocio('?')}`, {});
    return res.ok ? res : clasificar(res);
  },

  /**
   * (8) GET /{id}/pdf -- el PDF ya guardado, para entregarlo de verdad.
   *
   * Lo esta construyendo Argy-A (ASSIGN #8526). Se pide como blob porque lo que
   * vuelve es un archivo, no JSON. Mientras no exista responde 404 y el boton lo
   * dice honesto en vez de quedarse mudo.
   */
  async descargarPdf(id) {
    if (!id) return { ok: false, error: { code: 'ID_REQUERIDO' } };
    try {
      const headers = {};
      if (AppConfig.token) headers['Authorization'] = `Bearer ${AppConfig.token}`;
      const res = await fetch(`${AppConfig.apiBase}${BASE}/${encodeURIComponent(id)}/pdf${paramNegocio('?')}`, { headers });
      if (res.ok) return { ok: true, blob: await res.blob() };
      if (res.status === 404) return { ok: false, error: { code: 'NOT_IMPLEMENTED' } };
      if (res.status === 401) return { ok: false, error: { code: 'SIN_SESION' } };
      if (res.status === 403) return { ok: false, error: { code: 'FORBIDDEN' } };
      return { ok: false, error: { code: String(res.status) } };
    } catch (e) {
      return { ok: false, error: { code: 'NETWORK', message: String(e).slice(0, 120) } };
    }
  },

  /** (5) POST /{id}/solicitar-pdf -- marca listo; el render llega en fase B. */
  async solicitarPdf(id) {
    if (!id) return { ok: false, error: { code: 'ID_REQUERIDO' } };
    const res = await ApiBase.post(`${BASE}/${encodeURIComponent(id)}/solicitar-pdf${paramNegocio('?')}`, {});
    return res.ok ? res : clasificar(res);
  },

  /**
   * (6) POST /upload -- recibe el PDF del cliente y lo deja EN COLA.
   * Fase A NO procesa el PDF: la promesa al cliente es "en 24h lo tienes montado".
   */
  async subirPdf(file) {
    if (!file) return { ok: false, error: { code: 'ARCHIVO_REQUERIDO' } };
    // ApiBase SIEMPRE hace JSON.stringify y fuerza Content-Type json (ApiBase:17-25):
    // con FormData enviaria {} y el header equivocado. Por eso fetch directo, igual
    // que el upload del OCR (ComprasApiClient.uploadOcr) pero CON token, porque este
    // endpoint es de la API y esta gateado. No se toca ApiBase: es codigo compartido.
    const fd = new FormData();
    fd.append('archivo', file, file.name);
    const headers = {};
    if (AppConfig.token) headers['Authorization'] = `Bearer ${AppConfig.token}`;
    try {
      const res = await ApiBase.fetchQueCambia(`${AppConfig.apiBase}${BASE}/upload${paramNegocio('?')}`, { method: 'POST', body: fd, headers });
      if (res.ok) return { ok: true, data: await res.json().catch(() => ({})) };
      if (res.status === 404) return { ok: false, error: { code: 'NOT_IMPLEMENTED' } };
      // 401 y 403 NO son lo mismo y confundirlos costo un ensayo de demo:
      // el panel decia "no tienes permiso" cuando el problema era la SESION.
      // 401 = no estas identificado (o el token vencio). 403 = te identificaste
      // pero no te alcanza el rol.
      if (res.status === 401) return { ok: false, error: { code: 'SIN_SESION' } };
      if (res.status === 403) return { ok: false, error: { code: 'FORBIDDEN' } };
      // La API ya dice POR QUE rechazo (MUY_GRANDE / NO_ES_PDF / SIN_ARCHIVO) en el
      // ProblemDetails: `type` termina en el codigo. Antes se tiraba a la basura y
      // todo 400 acababa en "intenta de nuevo en un momento" -- que para un archivo
      // muy grande es mentira: reintentar nunca va a funcionar.
      const cuerpo = await res.json().catch(() => null);
      return { ok: false, error: { code: codigoDelProblema(cuerpo, res.status), detalle: cuerpo?.detail ?? cuerpo?.Detail ?? '' } };
    } catch (e) {
      return { ok: false, error: { code: 'NETWORK', message: String(e).slice(0, 120) } };
    }
  }
};
